import {takeUntil} from 'rxjs/operators';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnInit,
    Output,
    forwardRef,
    Optional,
    HostBinding,
    ViewChild,
    AfterViewInit
} from '@angular/core';
import {FormGroupDirective} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {TRANSLATION_BASE} from 'app/constants/injection-tokens';
import * as fns from 'date-fns';
import {DiFormElement} from 'app/common/di-forms/shared/di-form-element';
import {DiFormConfigDirective} from 'app/common/di-forms/directives/di-form.directive';
import {NzI18nService, en_GB, fr_FR} from 'ng-zorro-antd/i18n';
import {faInfoCircle} from '@fortawesome/free-solid-svg-icons';
import {DEFAULT_DATE_FORMAT} from 'app/app.constants';
import {RoleService} from 'app/blocks/service/api/role.service';
import {NzDatePickerComponent} from 'ng-zorro-antd/date-picker';
import {ɵDatePickerService as DatePickerService} from 'ng-zorro-antd/date-picker';
import {CandyDate} from 'ng-zorro-antd/core/time';
import {LogService} from 'app/blocks/service/log.service';
import {debounce} from 'lodash';

export const formElementProvider: any = {
    provide: DiFormElement,
    useExisting: forwardRef(() => DiDateComponent)
};

@Component({
    selector: 'di-date',
    template: `
        <nz-form-item [formGroup]="thisFormGroup" [ngStyle]="{'justify-content': getPosition()}" *ngIf="this.showField">
            <div
                [ngStyle]="{
                    width: width,
                    display: labelRowAlign ? 'flex' : 'initial',
                    'justify-content': labelRowAlign ? 'space-evenly' : 'initial'
                }">
                <nz-form-label
                    [ngStyle]="{
                        minWidth: labelRowAlign ? '45%' : 'auto',
                        display: labelRowAlign ? 'flex' : 'initial'
                    }"
                    *ngIf="!suppressLabel"
                    [nzFor]="fieldLabel$ | async"
                    [nzRequired]="fieldIsRequired"
                    [nzNoColon]="true">
                    <span [ngClass]="field">
                        {{ useCustomFieldLabel ? customFieldLabel : (fieldLabel$ | async) }}
                        <ng-container *ngIf="fieldHint$ | async as hint"
                            ><fa-icon class="di-mhint-icon" [icon]="faInfoIcon" size="xs" nz-tooltip [nzTooltipTitle]="hint"></fa-icon
                            ><span class="di-mfield-hint">{{ hint }}</span></ng-container
                        >
                    </span>
                </nz-form-label>
                <nz-form-control [nzErrorTip]="suppressError ? null : (validationError$ | async)">
                    <nz-date-picker
                        #datePicker
                        id="{{ 'di-date-' + field + idSuffix }}"
                        [ngStyle]="{width: width, 'max-width': width, 'z-index': 10000}"
                        [nzAutoFocus]="focus"
                        formControlName="{{ field }}"
                        nzFormat="{{ format }}"
                        [nzDisabled]="disabled"
                        [nzDisabledDate]="disabledDate"
                        (ngModelChange)="dateChanged($event)"
                        [nzAllowClear]="allowClear"
                        [nzShowTime]="showTime"
                        [nzPlaceHolder]="placeHolder"
                        (nzOnOpenChange)="onOpenChange($event)"
                        (wheel)="onWheelScroll($event)">
                    </nz-date-picker>
                </nz-form-control>
            </div>
        </nz-form-item>
    `,
    styleUrls: [`./di-date.component.scss`],
    providers: [formElementProvider],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DiDateComponent extends DiFormElement implements OnInit, AfterViewInit {
    @Input() @HostBinding('class.flex-grow') grow = true;
    @Input() width = '100%';
    @Input() align: 'start' | 'center' | 'end' = 'start';
    @Input() focus = false;
    @Input() labelRowAlign = false;
    @Input() customFieldLabel: string;
    @Input() useCustomFieldLabel = false;

    @Input() field: string;
    @Input() startDate: Date;
    @Input() suppressLabel = false;
    @Input() suppressError = false;
    @Input() minDate: any;
    @Input() maxDate: any;
    @Input() readonly = false;
    @Input() disabled = false;
    @Input() allowClear = true;
    @Input() idSuffix: string | number = '';
    @Input() roleDependent = false;
    @Input() showField = true;
    @Input() roleSettingSuffix = '';
    @Input() format = 'dd/MM/yyyy';
    @Input() showTime = false;
    @Input() placeHolder = 'Select Date';
    @Input() enableTracking = false;
    @Input() disabledDate = (date: Date) => this.disabledDateDefault(date);

    // @Input() customErrorStateMatcher: ErrorStateMatcher;

    @Output() dateChange: EventEmitter<any> = new EventEmitter<any>();

    faInfoIcon = faInfoCircle;
    datePickerService: DatePickerService;

    readonly dateFormat = DEFAULT_DATE_FORMAT;

    focused = false;

    @ViewChild('datePicker') datePicker: NzDatePickerComponent;

    constructor(
        @Inject(TRANSLATION_BASE) translationBase,
        translateService: TranslateService,
        fgDirective: FormGroupDirective,
        @Optional() diForm: DiFormConfigDirective,
        private i18n: NzI18nService,
        private _logService: LogService,
        protected roleService: RoleService
    ) {
        super(translationBase, translateService, fgDirective, roleService, diForm);

        if (this._translateService.currentLang !== 'en') {
            this.i18n.setLocale(fr_FR);
        } else {
            this.i18n.setLocale(en_GB);
        }

        translateService.onLangChange.pipe(takeUntil(this._unsubscribeAll)).subscribe((lang) => {
            if (this._translateService.currentLang !== 'en') {
                this.i18n.setLocale(fr_FR);
            } else {
                this.i18n.setLocale(en_GB);
            }
        });
    }

    ngAfterViewInit(): void {
        this.datePicker.onInputChange = this.onInputChange;
    }

    private debouncedLog = debounce((value) => {
        this._logService.log('Date Change', {
            field: this._fieldPath,
            value: value
        });
    }, 500);

    onInputChange(date, enter?) {
        if (typeof date === 'string') {
            const isFormattedDate = date.includes('/');
            // handle both raw DDMMYY and formatted DD/MM/YYYY formats
            if ((date.length === 6 && !isFormattedDate) || (date.length === 10 && isFormattedDate)) {
                const day = parseInt(date.slice(0, 2), 10);
                const month = isFormattedDate ? parseInt(date.slice(3, 5), 10) : parseInt(date.slice(2, 4), 10);
                const year = isFormattedDate ? parseInt(date.slice(6, 10), 10) : 2000 + parseInt(date.slice(4, 6), 10);

                if (!isNaN(month) && !isNaN(day) && !isNaN(year)) {
                    const candyDate = new CandyDate(new Date(year, month - 1, day));
                    this.datePickerService.setValue(candyDate);
                }
            }
        }
    }

    writeValue(value: any): void {
        // Implement the writeValue method here
    }

    getFieldName(): string {
        return this.field;
    }

    getPosition(): string {
        if (this.align !== 'center') {
            return 'flex-' + this.align;
        }
        return this.align;
    }

    dateChanged(newDate: any): void {
        // console.log(newDate);
        this.dateChange.emit(newDate);
        if (this.enableTracking) {
            this.debouncedLog(newDate);
        }
    }

    disabledDateDefault = (currentSelection: Date): boolean => {
        let disabled = false;
        if (this.minDate) {
            disabled = fns.differenceInCalendarDays(this.minDate, currentSelection) > 0;
        }
        if (!disabled && this.maxDate) {
            disabled = fns.differenceInCalendarDays(currentSelection, this.maxDate) > 0;
        }
        return disabled;
    };

    convertToDate(value: string) {
        console.log(value);
        if (value.length === 6) {
            const day = parseInt(value.slice(0, 2), 10);
            const month = parseInt(value.slice(2, 4), 10);
            const year = 2000 + parseInt(value.slice(4, 6), 10);

            if (!isNaN(month) && !isNaN(day) && !isNaN(year)) {
                const date = new Date(year, month - 1, day);
                this._thisControl.patchValue(date);
                this.dateChange.emit(date);
            }
        }
    }

    onOpenChange(event) {
        if (event) {
            this.datePicker.pickerInput.nativeElement.select();
            this.focused = true;
        } else {
            this.focused = false;
        }
    }

    onWheelScroll(event) {
        if (!this.focused) {
            return;
        }
        event.preventDefault();
        const date = new Date(this._thisControl.value);
        if (event.deltaY > 0) {
            this._thisControl.patchValue(new Date(date.setDate(date.getDate() - 1)));
        } else {
            this._thisControl.patchValue(new Date(date.setDate(date.getDate() + 1)));
        }
    }
}
